import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FilePlus2, ArrowLeft, AlertTriangle } from "lucide-react";
import { api, errMsg, fmt } from "../lib/api";
import { PageHeader, Panel, Qty } from "../components/shared";

const STATUS = { overdue: "text-[#EF4444]", due: "text-[#F59E0B]", upcoming: "text-[#38BDF8]" };

const REASON = { routine: ["PAR / ADCR", "bg-[#38BDF8]/15 text-[#38BDF8]"], production_plan: ["PRODUCTION PLAN", "bg-[#10B981]/15 text-[#10B981]"] };
const ReasonTag = ({ r }) => (
  <span data-testid={`reason-${r}`} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm mr-1 ${(REASON[r] || [])[1] || "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
    {(REASON[r] || ["PRODUCTION"])[0]}
  </span>
);

function Line({ l, on, onToggle }) {
  return (
    <div data-testid={`suggest-line-${l.sku}`} className={`flex justify-between gap-3 py-3 border-t border-[#1E293B] text-sm ${on ? "" : "opacity-50"}`}>
      <input type="checkbox" data-testid={`suggest-check-${l.sku}`} className="w-5 h-5 mt-0.5 shrink-0 accent-[#F59E0B]" checked={on} onChange={onToggle} />
      <div className="min-w-0 flex-1">
        <div className="truncate">{l.name}</div>
        <div className="mt-1">{l.reasons.map((r) => <ReasonTag key={r} r={r} />)}</div>
        {l.reasons.includes("routine") && (
          <div className="font-mono text-[11px] text-[#9CA3AF] mt-1" data-testid={`suggest-routine-${l.sku}`}>
            ADCR {fmt(l.adcr)}/d ({l.adcr_window_days}d) · DOS {fmt(l.dos_days)}d · {l.days_of_cover != null ? `${fmt(l.days_of_cover)}d cover` : "no usage"} · ROP {fmt(l.reorder_point)} ·{" "}
            <span className={STATUS[l.status]}>ORDER BY {l.order_by}{l.status === "overdue" ? " (OVERDUE)" : ""}</span>
          </div>
        )}
        {l.plans?.length > 0 && (
          <div className="font-mono text-[11px] text-[#10B981] mt-1" data-testid={`suggest-plans-${l.sku}`}>
            PLANS NEED {fmt(l.plan_qty)} {l.unit}: {l.plans.map((p) => `${p.name} ${fmt(p.qty)} (due ${p.target_date})`).join(" · ")} · ORDER BY {l.order_by}{l.status === "overdue" ? " (OVERDUE)" : ""}
          </div>
        )}
        <div className="font-mono text-[11px] text-[#6B7280]">
          <span data-testid={`suggest-extsku-${l.sku}`} className="text-[#E5E7EB]">{l.ext_sku || l.sku}</span>{l.ext_sku && l.ext_sku !== l.sku ? ` (ours ${l.sku})` : ""}{l.short != null ? ` · short ${fmt(l.short)}` : ""} · stock {fmt(l.stock)}{l.on_order_applied > 0 ? ` · ${fmt(l.on_order_applied)} on order` : ""} · {l.packs} × {fmt(l.order_unit_qty)}
        </div>
      </div>
      <div className="text-right shrink-0"><Qty v={l.recommended_order} unit={l.unit} className="font-bold" /><div className="font-mono text-[11px] text-[#6B7280]">${l.line_total.toFixed(2)}</div></div>
    </div>
  );
}

function Draft({ po, creating, onCreate }) {
  const key = po.supplier_id || "unassigned";
  const [off, setOff] = useState(new Set());
  const sel = po.lines.filter((l) => !off.has(l.item_id));
  const total = sel.reduce((a, l) => a + l.line_total, 0);
  const all = off.size === 0;
  const flip = (id) => setOff((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <Panel className="p-5" data-testid={`suggest-po-${key}`}>
      <div className="flex justify-between items-start gap-2 mb-3">
        <div>
          <div className="font-display text-lg font-bold">{po.supplier}</div>
          <div className="font-mono text-xs text-[#6B7280]">{po.lead_time_days != null ? `LEAD ${po.lead_time_days}d · ` : ""}NEEDED BY {po.needed_by}</div>
        </div>
        <div className="text-right"><div className="font-mono text-[10px] text-[#6B7280]">EST. TOTAL</div><div className="font-mono text-xl font-bold text-[#38BDF8]" data-testid={`suggest-total-${key}`}>${total.toFixed(2)}</div></div>
      </div>
      {po.late && <div className="mb-3 p-2 rounded bg-[#F59E0B]/10 text-xs text-[#F59E0B] flex items-center gap-2" data-testid={`suggest-late-${key}`}><AlertTriangle className="w-4 h-4" />Past the order-by date or inside supplier lead time. Order now or expedite.</div>}
      <label className="flex items-center gap-2 py-2 text-xs font-mono text-[#9CA3AF] cursor-pointer">
        <input type="checkbox" data-testid={`suggest-select-all-${key}`} className="w-5 h-5 accent-[#F59E0B]" checked={all} onChange={() => setOff(all ? new Set(po.lines.map((l) => l.item_id)) : new Set())} />
        SELECT ALL · {sel.length}/{po.lines.length}
      </label>
      {po.lines.map((l) => <Line key={l.item_id} l={l} on={!off.has(l.item_id)} onToggle={() => flip(l.item_id)} />)}
      <button data-testid={`suggest-create-po-${key}`} disabled={!!creating || !sel.length} onClick={() => onCreate({ ...po, lines: sel })}
        className="mt-4 w-full h-12 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center justify-center gap-2 disabled:opacity-40">
        <FilePlus2 className="w-4 h-4" />{creating === key ? "Creating…" : `Create PO (${sel.length} Selected)`}
      </button>
    </Panel>
  );
}

export default function OrderSuggestions() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(null);
  const sid = params.get("schedule");
  const day = params.get("date");
  const fromSchedule = !!(sid && day);

  useEffect(() => {
    const path = fromSchedule ? `/schedule/${sid}/days/${day}/order-suggestions` : "/ordering/required";
    api.get(path).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [sid, day, fromSchedule]);

  const create = async (po) => {
    setCreating(po.supplier_id || "unassigned");
    try {
      const r = await api.post("/purchase-orders", {
        supplier_id: po.supplier_id, expected_date: po.needed_by,
        notes: fromSchedule ? `Ordering required for ${day} (production + par/ADCR)` : "Routine replenishment (par/ADCR)",
        lines: po.lines.map((l) => ({ item_id: l.item_id, qty: l.recommended_order, unit_cost: l.unit_cost || 0 })),
      });
      toast.success(`${r.data.po_number} created`);
      nav(`/ordering?po=${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCreating(null); }
  };

  const title = fromSchedule ? (data ? `Orders for ${data.weekday} ${data.date}` : "Suggested Orders") : "Ordering Required";
  return (
    <div>
      <PageHeader kicker={fromSchedule ? "// ORDERING · FROM WEEKLY SCHEDULE" : "// ORDERING · ROUTINE REPLENISHMENT"} title={title}>
        {fromSchedule && <button data-testid="suggest-back-btn" onClick={() => nav("/schedule")} className="h-12 px-4 rounded-md border border-[#334155] font-semibold flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Back to Schedule</button>}
      </PageHeader>
      {error && <Panel className="p-6 text-[#EF4444]" data-testid="suggest-error">{error}</Panel>}
      {data && (
        <>
          <p className="text-sm text-[#9CA3AF] mb-6 max-w-3xl" data-testid="suggest-explainer">
            Raw materials and outsourced preproduction items are flagged when projected stock (on hand + on order) reaches its reorder point: par + ADCR × the item's lead time. ADCR comes from Shopify sales over the ADCR window set in Master Controls, broken down through each BOM. Each routine order covers the item's Days of Supply past the lead time.
            {fromSchedule && " Shortfalls from this day's production are added to the same POs. Where an item has both, the larger quantity is used."}
            {" "}Quantities are rounded up to pack size.
          </p>
          <div className="grid lg:grid-cols-2 gap-4" data-testid="suggest-po-drafts">
            {data.po_drafts.map((po) => <Draft key={po.supplier_id || "unassigned"} po={po} creating={creating} onCreate={create} />)}
            {!data.po_drafts.length && <Panel className="p-6 text-[#10B981] font-mono" data-testid="suggest-none">{fromSchedule ? "NO ORDERING REQUIRED FOR THIS DAY" : "NO ORDERING REQUIRED IN THE NEXT 7 DAYS"}</Panel>}
          </div>
        </>
      )}
    </div>
  );
}
